import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { DateTime } from "luxon";
import ApiHttp from "byzantine/src/ApiHttp";
import Transaction from "byzantine/src/Transaction";
import type { TransactionResponse } from "byzantine/src/Transaction";
import type Account from "byzantine/src/Account";
import { useNotificationContext } from "cerulean";
import mergeTransactions from "./mergeTransactions";

/**
 * Cursor pagination clamps `per_page` to DRF's `offset_cutoff` (1000) rather
 * than `MAX_PAGE_SIZE` (250), which page-number pagination is capped at. We
 * deliberately omit `page` so the server picks the cursor paginator.
 */
const INTERNAL_PER_PAGE = 1000;

/** `external_transactions` uses page-number pagination, capped at `MAX_PAGE_SIZE`. */
const EXTERNAL_PER_PAGE = 250;

/**
 * Shared ceiling on rows pulled from a single source. Expressed in rows rather
 * than pages so the two sources get the same limit despite their different page
 * sizes, and doubles as the guard against a malformed `links.next` looping
 * forever.
 */
const MAX_TRANSACTIONS = 25_000;

const DAYS_OF_HISTORY = 365;

export type TransactionSourceType = "internal" | "external" | "all";

type TransactionsResponse = {
  transactions?: TransactionResponse[];
  external_transactions?: TransactionResponse[];
  links?: { next: string | null };
};

type TransactionSource = {
  /** Key the rows live under in the paginated response. */
  dataKey: "transactions" | "external_transactions";
  perPage: number;
  buildEndpoint: (since: string) => string;
};

/**
 * `settled` maps to `post_datetime` (serialized as `settled_at`). The `>=`
 * comparison is only implemented for quoted values, so the timestamp must stay
 * wrapped in double quotes. `is:settled` also lets the server skip its
 * per-account pending-transaction fetch, which hits the core.
 */
const INTERNAL_SOURCE: TransactionSource = {
  dataKey: "transactions",
  perPage: INTERNAL_PER_PAGE,
  buildEndpoint: (since) => {
    const query = `is:settled settled>="${since}"`;
    return `transactions?q=${encodeURIComponent(query)}`;
  },
};

/**
 * The external filterset has no `q=` support, but `settled_from` maps to
 * `post_datetime__gte`, and `>=` against a nullable column excludes unsettled
 * rows -- so it covers both halves of the internal query on its own.
 */
const EXTERNAL_SOURCE: TransactionSource = {
  dataKey: "external_transactions",
  perPage: EXTERNAL_PER_PAGE,
  buildEndpoint: (since) =>
    `external_transactions?settled_from=${encodeURIComponent(since)}`,
};

/**
 * Walks `links.next` to exhaustion, pushing deserialized rows into `collected`.
 *
 * Never rejects: a failure is reported through the return value so one source
 * erroring can't cut the other's walk short, and whatever paged in before the
 * failure is left in `collected` so the source degrades to a partial year
 * rather than to nothing.
 *
 * @returns `true` when the walk failed.
 */
const fetchAllPages = async (
  source: TransactionSource,
  since: string,
  accounts: Account[],
  collected: Transaction[],
  shouldCancel: () => boolean
): Promise<boolean> => {
  const maxPages = Math.ceil(MAX_TRANSACTIONS / source.perPage);
  let endpoint = source.buildEndpoint(since);
  let isEndpointUrl = false;

  try {
    for (let page = 0; page < maxPages; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const response: TransactionsResponse = await ApiHttp.fetch(
        endpoint,
        { method: "GET", endpointIsUrl: isEndpointUrl },
        isEndpointUrl ? {} : { per_page: source.perPage }
      );

      if (shouldCancel()) return false;

      collected.push(
        ...(response?.[source.dataKey] || []).map((t) =>
          Transaction.deserialize(
            t,
            accounts.find((a) => a.id === t.account_id)
          )
        )
      );

      const next = response?.links?.next;
      if (!next) break;

      // `links.next` already carries the cursor/page and `per_page`.
      endpoint = next;
      isEndpointUrl = true;
    }
    return false;
  } catch {
    return true;
  }
};

/**
 * Fetches every settled transaction from the last rolling 365 days.
 *
 * `useSettledTransactions` caps out at a single 100-row page, which is too few
 * to drive a year of spend/income breakdowns, so this walks `links.next` to
 * exhaustion instead.
 *
 * @param accounts Used to attach the owning account to each row, and to decide
 *   whether the external feed is worth requesting.
 * @param type Which feeds to pull from. Defaults to `internal`.
 */
const useYearTransactions = (
  accounts: Account[],
  type: TransactionSourceType = "internal"
) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const [isLoading, setIsLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  useEffect(() => {
    let isCancelled = false;

    const fetchAllTransactions = async () => {
      const internal: Transaction[] = [];
      const external: Transaction[] = [];
      const wantsInternal = type !== "external";
      // Skip the round trip entirely when nothing is linked.
      const wantsExternal =
        type !== "internal" && accounts.some((a) => !a.isInternal());

      try {
        setIsLoading(true);

        const since = DateTime.local()
          .minus({ days: DAYS_OF_HISTORY })
          .startOf("day")
          .toISO();

        const failures = await Promise.all([
          wantsInternal &&
            fetchAllPages(
              INTERNAL_SOURCE,
              since,
              accounts,
              internal,
              () => isCancelled
            ),
          wantsExternal &&
            fetchAllPages(
              EXTERNAL_SOURCE,
              since,
              accounts,
              external,
              () => isCancelled
            ),
        ]);

        if (isCancelled) return;

        setTransactions(mergeTransactions(internal, external));

        // One notification whether one or both sources failed.
        if (failures.some(Boolean)) {
          sendNotification({
            type: "negative",
            text: l10n.getString("recent-transactions-error"),
          });
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    fetchAllTransactions();

    return () => {
      isCancelled = true;
    };
    // Only re-fetch when account IDs change (accounts added/removed),
    // not when account properties like favorited or hidden change.
  }, [
    accounts
      .map((a) => a.id)
      .slice()
      .sort()
      .join(","),
    type,
  ]);

  return { isLoading, transactions };
};

export default useYearTransactions;
